import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronRight,
  FileSignature,
  Paperclip,
  Printer,
  Upload,
  Download,
  MessageSquarePlus,
  X,
  CircleDot,
  ShieldCheck,
  Undo2,
  User as UserIcon,
  Search,
  ArrowUpDown,
  Filter,
  LayoutGrid,
  Table2,
  RotateCcw,
  Briefcase,
  Info,
  Trash2,
  History,
  Sparkles,
  AlertTriangle,
  TriangleAlert,
} from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Modal } from "@/components/ui/Modal";
import { useContractorPreview } from "@/features/orders/contractorPreview";
import {
  useWpwHandovers,
  useWpwHandoverMutations,
  useWpwNeverSentParcels,
  useTaskPolling,
} from "../hooks";
import {
  startWpwAgreementAi,
  startWpwHandoverZip,
  startWpwOrderAgreementAi,
  startWpwPackagesZip,
} from "../api";
import {
  WPW_DECLARED_STATUS_LABEL,
  WPW_DECLARED_STATUS_COLOR,
  WPW_DECISION_LABEL,
  WPW_SELECTABLE_STATUSES,
  WPW_ACQUISITION_STATUSES,
  WPW_PROBLEM_STATUSES,
  WPW_AWAITING_FILE_STATUS,
  WPW_AI_SEVERITY_LABEL,
  type WpwHandover,
  type WpwHandoverEvent,
  type WpwHandoverFile,
  type WpwDeclaredStatus,
  type WpwDecision,
  type WpwAgreementAiAnalysis,
  type WpwAiFinding,
  type WpwAiSeverity,
  actionableAiFindings,
} from "../types";

function fmt(dt: string | null): string {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dt;
  }
}

function extractError(err: unknown): string {
  const data = (
    err as { response?: { data?: { errors?: { detail?: string } } } }
  )?.response?.data;
  return data?.errors?.detail ?? "Nie udało się zapisać.";
}

// „Pobierz ZIP" errors arrive in the standard {detail} envelope; a plain-string
// `errors` (older shape) is accepted too.
function extractZipError(err: unknown): string {
  const errors = (err as { response?: { data?: { errors?: unknown } } })?.response
    ?.data?.errors;
  if (typeof errors === "string" && errors) return errors;
  const detail = (errors as { detail?: unknown } | null | undefined)?.detail;
  if (typeof detail === "string" && detail) return detail;
  return "Nie udało się spakować plików.";
}

function triggerDownload(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ── sort/filter helpers ───────────────────────────────────────────────────────
const VIEW_LS_KEY = "wpw-handover-view-mode";

const ALL_STATUSES: WpwDeclaredStatus[] = [
  "not_started",
  "in_progress",
  "collected_in_field",
  "problem_refusal",
  "problem_impossible",
  "consent_written",
  "decision_art124",
];
const STATUS_ORDER: Record<WpwDeclaredStatus, number> = {
  not_started: 0,
  in_progress: 1,
  collected_in_field: 2,
  problem_refusal: 3,
  problem_impossible: 4,
  consent_written: 5,
  decision_art124: 6,
};

type SortField =
  | "owner"
  | "status"
  | "parcel"
  | "updated"
  | "decision"
  | "files";
const SORT_LABELS: Record<SortField, string> = {
  owner: "Właściciel",
  status: "Status",
  parcel: "Działka",
  updated: "Ostatnia zmiana",
  decision: "Decyzja GK",
  files: "Liczba plików",
};

type DecisionFilter = "all" | WpwDecision | "none";
type FilesFilter = "all" | "with" | "without";

function activeFileCount(h: WpwHandover): number {
  return h.attachments.filter((f) => f.state !== "removed").length;
}
function lastActivity(h: WpwHandover): number {
  const s = h.declared_status_at ? Date.parse(h.declared_status_at) : 0;
  const t = h.timeline.length
    ? Date.parse(h.timeline[h.timeline.length - 1].changed_at)
    : 0;
  const ms = Math.max(Number.isFinite(s) ? s : 0, Number.isFinite(t) ? t : 0);
  return Number.isFinite(ms) ? ms : 0;
}
function decisionRank(h: WpwHandover): number {
  const d = h.current_confirmation?.decision;
  if (!d) return -1;
  return (
    (
      { revoked: 0, rejected: 1, needs_info: 2, confirmed: 3 } as Record<
        WpwDecision,
        number
      >
    )[d] ?? 0
  );
}

// Filtr statusów jako LISTA ROZWIJANA z wielokrotnym wyborem — kolory zostają te same,
// co na odznakach statusu, ale rząd kolorowych przycisków nie zjada już całej szerokości
// paska narzędzi. Pusty wybór = wszystkie statusy (żaden filtr).
function StatusFilterSelect({
  value,
  onChange,
}: {
  value: Set<WpwDeclaredStatus>;
  onChange: (next: Set<WpwDeclaredStatus>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const picked = ALL_STATUSES.filter((s) => value.has(s));
  const toggle = (s: WpwDeclaredStatus) => {
    const next = new Set(value);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    onChange(next);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Filtr statusów — można zaznaczyć kilka"
        className="input-base text-xs py-1 w-auto inline-flex items-center gap-1.5 max-w-[22rem]"
      >
        <span className="text-[var(--color-text-muted)]">Status:</span>
        {picked.length === 0 ? (
          <span className="text-[var(--color-text-muted)]">wszystkie</span>
        ) : (
          <span className="inline-flex items-center gap-1 min-w-0">
            {picked.slice(0, 2).map((s) => (
              <span
                key={s}
                className="px-1 py-px rounded text-[10px] font-semibold uppercase tracking-wide text-white whitespace-nowrap"
                style={{ backgroundColor: WPW_DECLARED_STATUS_COLOR[s] }}
              >
                {WPW_DECLARED_STATUS_LABEL[s]}
              </span>
            ))}
            {picked.length > 2 && (
              <span className="text-[10px] text-[var(--color-text-muted)] whitespace-nowrap">
                +{picked.length - 2}
              </span>
            )}
          </span>
        )}
        <ChevronDown size={13} className="shrink-0 text-[var(--color-text-muted)]" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 min-w-[15rem] rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-lg p-1">
          {ALL_STATUSES.map((s) => {
            const on = value.has(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggle(s)}
                className="flex w-full items-center gap-2 px-2 py-1 text-left text-xs hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
              >
                <span
                  className="w-3.5 h-3.5 border rounded-sm inline-flex items-center justify-center shrink-0"
                  style={{
                    borderColor: WPW_DECLARED_STATUS_COLOR[s],
                    backgroundColor: on ? WPW_DECLARED_STATUS_COLOR[s] : "transparent",
                  }}
                >
                  {on && <Check size={10} className="text-white" />}
                </span>
                <span
                  className="font-semibold uppercase tracking-wide text-[11px]"
                  style={{ color: WPW_DECLARED_STATUS_COLOR[s] }}
                >
                  {WPW_DECLARED_STATUS_LABEL[s]}
                </span>
              </button>
            );
          })}
          <div className="flex items-center justify-between border-t border-[var(--color-border-muted)] mt-1 pt-1 px-2">
            <button
              type="button"
              onClick={() => onChange(new Set())}
              disabled={picked.length === 0}
              className="inline-flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] hover:underline disabled:opacity-50 disabled:no-underline"
            >
              <RotateCcw size={11} /> Wyczyść status
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] text-[var(--color-primary)] hover:underline"
            >
              Gotowe
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Rozwijana sekcja karty właściciela (pliki negocjatora / dokumenty od GK). Nagłówek
// mówi, ile czego jest i czy coś czeka na reakcję, więc zwinięta sekcja niczego nie
// ukrywa; ponowny klik ZWIJA (żadne rozwinięcie w tej karcie nie jest jednokierunkowe).
function DisclosureSection({
  open,
  onToggle,
  icon,
  label,
  count,
  summary,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  icon: ReactNode;
  label: string;
  count?: number;
  summary?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="border-t border-[var(--color-border-muted)]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-1.5 px-3 py-1.5 text-left text-xs text-[var(--color-text-muted)] hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
      >
        {open ? (
          <ChevronDown size={13} className="shrink-0" />
        ) : (
          <ChevronRight size={13} className="shrink-0" />
        )}
        {icon}
        <span className="font-medium text-[var(--color-text)]">{label}</span>
        {count !== undefined && <span className="font-mono">({count})</span>}
        {summary}
      </button>
      {open && <div className="px-3 pb-2.5 pt-1">{children}</div>}
    </div>
  );
}

// Rząd opcji rozdzielony PIONOWYMI KRESKAMI — „Dodaj plik | Dodaj porozumienie |
// Analiza AI | historia" czyta się inaczej niż ten sam ciąg rozdzielony samą spacją.
// `items` jedzie PROPEM, nie dziećmi: tablica w pozycji children każe Reactowi żądać
// kluczy od elementów, których tu nie ma kto nadać (to gotowe przyciski z FilesCell).
function OptionBar({ items }: { items: ReactNode[] }) {
  const shown = items.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center divide-x divide-[var(--color-border)]">
      {shown.map((item, i) => (
        <span key={i} className="px-2 first:pl-0 last:pr-0 inline-flex items-center">
          {item}
        </span>
      ))}
    </span>
  );
}

function StatusBadge({ status }: { status: WpwDeclaredStatus }) {
  return (
    <span
      className="px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide text-white"
      style={{ backgroundColor: WPW_DECLARED_STATUS_COLOR[status] }}
    >
      {WPW_DECLARED_STATUS_LABEL[status]}
    </span>
  );
}

function SideBadge({ side }: { side: "negotiator" | "internal" }) {
  const internal = side === "internal";
  return (
    <span
      className={`px-1 py-px rounded text-[9px] font-medium uppercase ${
        internal
          ? "bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
          : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
      }`}
    >
      {internal ? "GK" : "Negocjator"}
    </span>
  );
}

function eventLine(ev: WpwHandoverEvent): string {
  switch (ev.kind) {
    case "status_change":
      // the body carries the negotiator's reason (mandatory for problem statuses)
      return ev.body
        ? `Status: ${ev.to_status_display} — ${ev.body}`
        : `Status: ${ev.to_status_display}`;
    case "comment":
      return ev.body;
    case "file_added":
      return `Dodano plik: ${ev.attachment_name ?? ""}`;
    case "file_removed":
      return `Usunięto plik: ${ev.attachment_name ?? ""}`;
    case "confirmation": {
      // GK's verdict; a per-file entry names the document, and the body carries the reason
      // (mandatory for a rejection) — the negotiator reads it here.
      const label = ev.decision ? WPW_DECISION_LABEL[ev.decision] : ev.kind_display;
      const what = ev.attachment_name ? `${label}: ${ev.attachment_name}` : label;
      return ev.body ? `${what} — ${ev.body}` : what;
    }
    // gk_docs (pliki pakietu przekazanego w teren) — nazwy plików jadą w body
    case "gk_doc_added":
    case "gk_doc_removed":
      return ev.body || ev.kind_display;
    case "ai_analysis":
      return ev.body || ev.kind_display;
    default:
      return ev.kind_display;
  }
}

// ── Analiza AI porozumień ─────────────────────────────────────────────────────
// Uwagi czyta z wgranych skanów model, uruchamia je GK ELEKTIM, a widzą je OBIE strony —
// także podwykonawca, który te pliki wgrał, bo to on ma je poprawić. Dlatego każda lista
// uwag jedzie pod banerem „wygenerowane przez AI": nic tu nie zmienia statusu, decyzję
// o przyjęciu porozumienia (✓/✗ przy pliku) nadal podejmuje człowiek.
const AI_SEVERITY_STYLE: Record<WpwAiSeverity, string> = {
  critical:
    "border-red-400 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
  warning:
    "border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
  info: "border-[var(--color-border-muted)] bg-black/[0.02] text-[var(--color-text-muted)] dark:bg-white/[0.04]",
};

function severityIcon(s: WpwAiSeverity) {
  if (s === "critical") return <AlertTriangle size={12} className="mt-0.5 shrink-0" />;
  if (s === "warning") return <TriangleAlert size={12} className="mt-0.5 shrink-0" />;
  return <Info size={12} className="mt-0.5 shrink-0" />;
}

function countCritical(a: WpwAgreementAiAnalysis) {
  return a.findings.filter((f) => f.severity === "critical").length;
}

// Baner odautorski — musi stać przy KAŻDEJ liście uwag, także u podwykonawcy.
function AiDisclaimer() {
  return (
    <p className="flex items-start gap-1.5 text-[10px] leading-relaxed text-[var(--color-text-muted)]">
      <Sparkles size={11} className="mt-0.5 shrink-0 text-fuchsia-500" />
      <span>
        <strong>Funkcja w fazie eksperymentalnej — AI może się mylić.</strong> Uwagi
        wygenerowane automatycznie przez <strong>sztuczną inteligencję</strong> na
        podstawie wgranych skanów. Mogą być niepełne lub błędne i{" "}
        <strong>nie są decyzją GK ELEKTIM</strong> — dokument ocenia człowiek; podstawę
        każdej uwagi sprawdź w „Co AI odczytało ze skanu". W razie wątpliwości napisz
        komentarz w tym WPW.
      </span>
    </p>
  );
}

function AiFindingRow({ f }: { f: WpwAiFinding }) {
  return (
    <li className={`flex items-start gap-1.5 border px-2 py-1.5 text-xs ${AI_SEVERITY_STYLE[f.severity]}`}>
      {severityIcon(f.severity)}
      <div className="min-w-0">
        <div className="font-medium break-words">{f.title}</div>
        {f.detail && <div className="text-[11px] break-words opacity-90">{f.detail}</div>}
        <div className="text-[10px] uppercase tracking-wide opacity-70">
          {WPW_AI_SEVERITY_LABEL[f.severity]}
          {f.file_name ? ` · ${f.file_name}` : ""}
        </div>
      </div>
    </li>
  );
}

// Znacznik przy plikach: „AI: 3 uwagi" / „AI: bez uwag". Widoczny dla obu stron.
function AiStatusChip({ a }: { a: WpwAgreementAiAnalysis }) {
  const count = actionableAiFindings(a.findings).length;
  const cls =
    a.status === "error"
      ? "border-[var(--color-border-muted)] text-[var(--color-text-muted)]"
      : countCritical(a) > 0
        ? "border-red-400 bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
        : count > 0
          ? "border-amber-400 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
          : "border-green-500 bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300";
  const label =
    a.status === "error"
      ? "AI: błąd analizy"
      : count > 0
        ? `AI: ${count} uwag(i)`
        : "AI: bez uwag";
  const detail =
    `Analiza AI dokumentów — ${a.status_display}` +
    (a.analyzed_at ? ` · ${fmt(a.analyzed_at)}` : "") +
    (a.is_stale ? " · po analizie wgrano nowe pliki" : "") +
    (a.summary ? `\n${a.summary}` : "");
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border shrink-0 whitespace-nowrap ${cls}`}
      title={detail}
    >
      <Sparkles size={9} /> {label}
      {a.is_stale && " ⚠"}
    </span>
  );
}

// Podstawa werdyktu: co model przepisał ze skanu (osoby z komparycji, każdy podpis, data).
// To jest to, co człowiek sprawdza ze skanem, zanim komuś uwierzy — zwinięte, żeby nie
// przykrywać uwag, ale zawsze pod ręką.
function AiEvidence({ a }: { a: WpwAgreementAiAnalysis }) {
  const ev = a.evidence;
  const sigs = ev?.signatures ?? [];
  const persons = ev?.persons_named ?? [];
  if (!ev || (sigs.length === 0 && persons.length === 0 && !ev.signing_date_as_written)) return null;
  return (
    <details className="text-[11px] text-[var(--color-text-muted)]">
      <summary className="cursor-pointer select-none">
        Co AI odczytało ze skanu: {persons.length} os. w komparycji · {sigs.length} podpis(ów)
        {ev.signing_date_as_written ? ` · data ${ev.signing_date_as_written}` : ""}
      </summary>
      <div className="mt-1 space-y-1 pl-2 border-l border-[var(--color-border-muted)]">
        {persons.length > 0 && (
          <div>
            <span className="font-medium">Podmiot Uprawniony wg komparycji:</span>{" "}
            {persons.map((p) => `${p.name}${p.pesel_as_written ? ` (PESEL ${p.pesel_as_written})` : ""}`).join("; ")}
          </div>
        )}
        {sigs.length > 0 && (
          <ul className="space-y-0.5">
            {sigs.map((s, i) => (
              <li key={i} className="break-words">
                <span className="font-mono">str. {s.page}</span>
                {s.on_map ? " · mapa" : ""} · {s.where} → „{s.letters_read}” = {s.person}
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

// Pełny wynik dla JEDNEGO WPW — w rozwinięciu wiersza/karty i w sekcji zbiorczej.
function AiFindings({ a, heading }: { a: WpwAgreementAiAnalysis; heading?: ReactNode }) {
  const actionable = actionableAiFindings(a.findings);
  // Techniczny komunikat błędu dostają tylko użytkownicy wewnętrzni — podgląd GK ma
  // pokazać to, co widzi podwykonawca (sam komunikat ogólny).
  const { isPreview } = useContractorPreview();
  const errorMessage = isPreview ? null : a.error_message;
  return (
    <div className="space-y-1.5">
      {heading}
      {a.summary && (
        <p className="text-xs text-[var(--color-text)] break-words">{a.summary}</p>
      )}
      {a.status === "error" ? (
        <p className="text-xs text-red-600 dark:text-red-400">
          {errorMessage || "Analiza AI nie powiodła się."}
        </p>
      ) : (
        <>
          {actionable.length === 0 && (
            <p className="text-xs text-green-700 dark:text-green-400">
              AI nie znalazło braków ani nieprawidłowości.
            </p>
          )}
          {a.findings.length > 0 && (
            <ul className="space-y-1">
              {a.findings.map((f, i) => (
                <AiFindingRow key={`${f.title}-${i}`} f={f} />
              ))}
            </ul>
          )}
          <AiEvidence a={a} />
        </>
      )}
      {a.is_stale && (
        <p className="text-[11px] text-amber-700 dark:text-amber-400">
          Uwaga: po tej analizie wgrano nowe pliki — wynik nie obejmuje kompletu dokumentów.
        </p>
      )}
      <p className="text-[10px] text-[var(--color-text-muted)]">
        Analiza z {fmt(a.analyzed_at)}
        {a.requested_by_name ? ` · zlecił(a): ${a.requested_by_name}` : ""}
        {a.model_used ? ` · model: ${a.model_used}` : ""}
        {a.analyzed_files.length
          ? ` · pliki: ${a.analyzed_files.map((f) => f.name).join(", ")}`
          : ""}
      </p>
      <AiDisclaimer />
    </div>
  );
}

// Zbiorcza lista uwag AI dla całego zamówienia — to jest ta „lista niedopatrzeń" w widoku
// zamówienia. Renderuje się identycznie po stronie GK i podwykonawcy; różnicy w treści nie
// ma, bo obie strony mają działać na tych samych uwagach.
function AiOrderFindings({ handovers }: { handovers: WpwHandover[] }) {
  // Jak w AiFindings: techniczny komunikat błędu tylko poza podglądem GK.
  const { isPreview } = useContractorPreview();
  const rows = handovers.filter(
    (h) =>
      h.ai_analysis &&
      (actionableAiFindings(h.ai_analysis.findings).length > 0 || h.ai_analysis.status === "error"),
  );
  const [open, setOpen] = useState(true);
  if (rows.length === 0) return null;
  const total = rows.reduce(
    (n, h) => n + (h.ai_analysis ? actionableAiFindings(h.ai_analysis.findings).length : 0),
    0,
  );
  const critical = rows.reduce((n, h) => n + countCritical(h.ai_analysis as WpwAgreementAiAnalysis), 0);
  const stale = rows.some((h) => h.ai_analysis?.is_stale);
  return (
    <div className="rounded border border-fuchsia-300 dark:border-fuchsia-900 bg-fuchsia-50/60 dark:bg-fuchsia-950/20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <Sparkles size={14} className="text-fuchsia-600 dark:text-fuchsia-400 shrink-0" />
        <span className="text-sm font-semibold text-[var(--color-text)]">
          Uwagi AI do przesłanych porozumień
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">
          {total} uwag(i) · {rows.length} właściciel(i)
        </span>
        {critical > 0 && (
          <span className="text-[9px] font-semibold uppercase tracking-wide px-1 py-px border border-red-400 bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {critical} poważn(e)
          </span>
        )}
        {stale && (
          <span
            className="text-[9px] font-semibold uppercase tracking-wide px-1 py-px border border-amber-400 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
            title="Po analizie wgrano nowe pliki — uruchom analizę ponownie"
          >
            nieaktualne
          </span>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <AiDisclaimer />
          {rows.map((h) => (
            <div key={h.id} className="space-y-1">
              <div className="flex flex-wrap items-center gap-x-2 text-xs font-medium text-[var(--color-text)]">
                <UserIcon size={12} className="text-[var(--color-text-muted)] shrink-0" />
                <span>{h.owner.full_name || "—"}</span>
                <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                  dz. {h.parcel.teryt}
                </span>
                {h.ai_analysis?.is_stale && (
                  <span className="text-[10px] text-amber-700 dark:text-amber-400">
                    (po analizie wgrano nowe pliki)
                  </span>
                )}
              </div>
              {h.ai_analysis?.status === "error" ? (
                <p className="text-xs text-red-600 dark:text-red-400 pl-4">
                  {(isPreview ? null : h.ai_analysis.error_message) || "Analiza AI nie powiodła się."}
                </p>
              ) : (
                <ul className="space-y-1 pl-4">
                  {actionableAiFindings(h.ai_analysis?.findings ?? []).map((f, i) => (
                    <AiFindingRow key={`${h.id}-${i}`} f={f} />
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Wspólna obsługa uruchomienia analizy (jeden WPW albo całe zamówienie): start zadania
// w tle, polling i odświeżenie listy WPW, gdy wynik jest gotowy.
function useAiAnalysisRun() {
  const qc = useQueryClient();
  const [taskId, setTaskId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const status = task.data?.status;
  useEffect(() => {
    if (!taskId || !status) return;
    if (status === "success") {
      setTaskId(null);
      qc.invalidateQueries({ queryKey: ["pp", "wpw-handovers"] });
    } else if (status === "failure" || status === "cancelled") {
      setError(task.data?.error?.split("\n")[0] || "Analiza AI nie powiodła się.");
      setTaskId(null);
      qc.invalidateQueries({ queryKey: ["pp", "wpw-handovers"] });
    }
  }, [status, taskId, task.data, qc]);
  const start = useMutation({
    mutationFn: (run: () => Promise<{ task_id: string }>) => run(),
    onSuccess: (r) => {
      setError(null);
      setTaskId(r.task_id);
    },
    onError: (e) => setError(extractZipError(e)),
  });
  return {
    error,
    running: start.isPending || !!taskId,
    run: (fn: () => Promise<{ task_id: string }>) => start.mutate(fn),
  };
}

// ZIP pakowany w tle (zgody od negocjatora, pakiety do druku): start zadania → polling →
// automatyczne pobranie archiwum, gdy gotowe.
function useBackgroundZip(startTask: () => Promise<{ task_id: string }>) {
  const [taskId, setTaskId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const downloadedRef = useRef<string | null>(null);
  const start = useMutation({
    mutationFn: startTask,
    onSuccess: (r) => {
      setError(null);
      downloadedRef.current = null;
      setTaskId(r.task_id);
    },
    onError: (e) => setError(extractZipError(e)),
  });
  const task = useTaskPolling(taskId);
  const status = task.data?.status;
  useEffect(() => {
    if (status === "success") {
      const rf = task.data?.result_file;
      if (rf?.url && downloadedRef.current !== rf.url) {
        downloadedRef.current = rf.url;
        triggerDownload(rf.url, rf.filename);
      }
      setTaskId(null);
    } else if (status === "failure" || status === "cancelled") {
      setError(task.data?.error?.split("\n")[0] || "Nie udało się spakować plików.");
      setTaskId(null);
    }
  }, [status, task.data]);
  return {
    error,
    running:
      start.isPending ||
      (!!taskId && !["success", "failure", "cancelled"].includes(status ?? "")),
    start: () => start.mutate(),
  };
}

// Shared action state for one WPW (one owner) — reused by both the card and the table row so the
// two views never drift apart.
function useHandoverActions(h: WpwHandover) {
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const m = useWpwHandoverMutations();
  // „Widok podwykonawcy" (GK): renderujemy warianty kontrolek negocjatora, tylko wyłączone.
  // Bez tego użytkownik wewnętrzny widzi w podglądzie sam odczyt i wnioskuje, że podwykonawca
  // nie ma jak zmienić statusu ani wgrać zgody — a ma.
  const { isPreview } = useContractorPreview();
  const canWrite = isPreview || h.can_write;
  const canConfirm = !isPreview && h.can_confirm;
  const busy =
    isPreview ||
    m.setStatus.isPending ||
    m.addComment.isPending ||
    m.addFiles.isPending ||
    m.removeFile.isPending ||
    m.removeGkDoc.isPending ||
    m.confirm.isPending ||
    m.unconfirm.isPending;
  const latestStatusEvent = [...h.timeline]
    .reverse()
    .find((e) => e.kind === "status_change");
  const isAcquisition = WPW_ACQUISITION_STATUSES.includes(h.declared_status);
  const conf = h.current_confirmation;
  const isConfirmed = conf?.decision === "confirmed";
  // A problem status never fires directly — the select opens a modal that forces a reason
  // (backend rejects a problem status without `note` anyway).
  const [pendingProblemStatus, setPendingProblemStatus] =
    useState<WpwDeclaredStatus | null>(null);

  function onStatusChange(value: string) {
    setError(null);
    if (WPW_PROBLEM_STATUSES.includes(value as WpwDeclaredStatus)) {
      setPendingProblemStatus(value as WpwDeclaredStatus);
      return;
    }
    m.setStatus.mutate(
      { id: h.id, status: value },
      { onError: (e) => setError(extractError(e)) },
    );
  }
  function submitProblem(note: string) {
    if (!pendingProblemStatus) return;
    setError(null);
    m.setStatus.mutate(
      { id: h.id, status: pendingProblemStatus, note },
      {
        onSuccess: () => setPendingProblemStatus(null),
        onError: (e) => setError(extractError(e)),
      },
    );
  }
  function uploadFile(file: File) {
    setError(null);
    m.addFiles.mutate(
      { id: h.id, files: [file] },
      { onError: (e) => setError(extractError(e)) },
    );
  }
  // Porozumienie: osobny rodzaj dokumentu — otwiera per-plikową decyzję GK i chip
  // „porozumienie do zatwierdzenia" na działce w projekcie.
  function uploadAgreement(file: File) {
    setError(null);
    m.addFiles.mutate(
      { id: h.id, files: [file], docKind: "porozumienie" },
      { onError: (e) => setError(extractError(e)) },
    );
  }
  function removeFile(f: WpwHandoverFile) {
    if (
      !window.confirm(
        `Usunąć plik „${f.original_name}”? Plik zostanie trwale usunięty — w historii ` +
          `pozostanie tylko wpis (nazwa, kto i kiedy).`,
      )
    )
      return;
    setError(null);
    m.removeFile.mutate(
      { id: h.id, fileId: f.id },
      { onError: (e) => setError(extractError(e)) },
    );
  }

  // Analiza AI wgranych dokumentów — uruchamia GK, wynik widzą obie strony.
  const ai = useAiAnalysisRun();
  function runAi() {
    ai.run(() => startWpwAgreementAi(h.id));
  }

  return {
    m,
    busy,
    canWrite,
    canConfirm,
    isPreview,
    comment,
    setComment,
    error,
    runAi,
    aiRunning: ai.running,
    aiError: ai.error,
    latestStatusEvent,
    isAcquisition,
    conf,
    isConfirmed,
    onStatusChange,
    uploadFile,
    uploadAgreement,
    removeFile,
    pendingProblemStatus,
    cancelProblem: () => setPendingProblemStatus(null),
    submitProblem,
  };
}

// Mandatory-reason modal for the two problem statuses. Saving is blocked on an empty
// reason; the text lands in the WPW timeline (STATUS_CHANGE body) with author + date.
function ProblemReasonModal({
  a,
  h,
}: {
  a: ReturnType<typeof useHandoverActions>;
  h: WpwHandover;
}) {
  const [note, setNote] = useState("");
  const open = a.pendingProblemStatus !== null;
  useEffect(() => {
    if (open) setNote("");
  }, [open]);
  if (!open || !a.pendingProblemStatus) return null;
  const saving = a.m.setStatus.isPending;
  return (
    <Modal open onClose={a.cancelProblem} title="Powód problemu" size="sm">
      <div className="space-y-3 text-sm">
        <p className="text-[var(--color-text)]">
          Zmieniasz status WPW dla{" "}
          <strong>{h.owner.full_name || "właściciela"}</strong> (dz.{" "}
          <span className="font-mono">{h.parcel.teryt}</span>) na:{" "}
          <strong style={{ color: WPW_DECLARED_STATUS_COLOR[a.pendingProblemStatus] }}>
            {WPW_DECLARED_STATUS_LABEL[a.pendingProblemStatus]}
          </strong>
        </p>
        <label className="block">
          <span className="text-xs font-medium text-[var(--color-text-muted)]">
            Opisz powód (obowiązkowe) — trafi do historii WPW i zobaczy go GK
          </span>
          <textarea
            className="input-base text-sm w-full min-h-[5rem] mt-1"
            autoFocus
            placeholder="Np. właściciel zmarł, spadkobiercy nieustaleni / właściciel przebywa za granicą…"
            value={note}
            disabled={saving}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        {a.error && <p className="text-red-500 text-xs">{a.error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="px-3 py-1.5 rounded text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            disabled={saving}
            onClick={a.cancelProblem}
          >
            Anuluj
          </button>
          <button
            type="button"
            className="px-3 py-1.5 rounded bg-red-600 text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
            disabled={saving || !note.trim()}
            onClick={() => a.submitProblem(note.trim())}
          >
            {saving ? "Zapisywanie…" : "Zapisz status z powodem"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// The reason behind the current problem status — surfaced inline (no expand needed) for
// everyone, with author + date from the timeline event that set it.
function ProblemReasonLine({
  h,
  latestStatusEvent,
  compact,
}: {
  h: WpwHandover;
  latestStatusEvent: WpwHandoverEvent | undefined;
  compact?: boolean;
}) {
  if (!WPW_PROBLEM_STATUSES.includes(h.declared_status)) return null;
  const ev =
    latestStatusEvent && latestStatusEvent.to_status === h.declared_status
      ? latestStatusEvent
      : undefined;
  if (!ev?.body) return null;
  return (
    <div
      className={`text-[11px] text-red-600 dark:text-red-400 ${compact ? "max-w-[16rem]" : "max-w-[22rem] text-right"}`}
      title={`${ev.body} — ${ev.author_name}, ${fmt(ev.changed_at)}`}
    >
      <span className="break-words">{ev.body}</span>{" "}
      <span className="text-[var(--color-text-muted)] whitespace-nowrap">
        — {ev.author_name}, {fmt(ev.changed_at)}
      </span>
    </div>
  );
}

// „Pakiet nieaktualny": GK otworzył NOWSZE zlecenie WPW na tę samą działkę i tego samego
// właściciela (zmiana trasy, przekompilowany pakiet, temat przekazany innemu negocjatorowi).
// Teren ma pracować na nowym pakiecie, a ten wiersz zostaje jako historia — obie strony
// widzą to pod statusem, z odnośnikiem do nowszego zlecenia. Negocjator dostaje link tylko
// do zlecenia własnej firmy (can_open); cudze widzi po numerze.
function SupersededLine({ h, compact }: { h: WpwHandover; compact?: boolean }) {
  const s = h.superseded_by;
  if (!s) return null;
  const label = s.order_number || "nowsze zlecenie";
  // `can_write` nadaje backend wyłącznie negocjatorowi tego zlecenia → panel podwykonawcy.
  // GK (także w „Widoku podwykonawcy") idzie do modułu zamówień.
  const href = h.can_write ? `/contractor/orders/${s.order_id}` : `/orders/${s.order_id}`;
  const title =
    `Nowsze zlecenie WPW na tę działkę i tego właściciela` +
    (s.contractor ? ` — ${s.contractor}` : "") +
    ` (${fmt(s.created_at)}, ${s.status_display})`;
  return (
    <div
      className={`text-[11px] text-amber-700 dark:text-amber-400 ${compact ? "max-w-[16rem]" : "text-right"}`}
      title={title}
    >
      <span className="inline-flex items-center gap-1 font-semibold">
        <TriangleAlert size={11} className="shrink-0" />
        pakiet nieaktualny
      </span>{" "}
      <span className="whitespace-nowrap">
        — nowsze zlecenie{" "}
        {s.can_open ? (
          <Link
            to={href}
            className="font-mono text-[var(--color-primary)] hover:underline"
            title="Otwórz nowsze zlecenie"
          >
            {label}
          </Link>
        ) : (
          <span className="font-mono">{label}</span>
        )}
      </span>
    </div>
  );
}

type Mutations = ReturnType<typeof useWpwHandoverMutations>;

// ── shared cell controls ──────────────────────────────────────────────────────
function StatusControl({
  h,
  busy,
  canWrite,
  onChange,
}: {
  h: WpwHandover;
  busy: boolean;
  canWrite: boolean;
  onChange: (v: string) => void;
}) {
  if (!canWrite) return <StatusBadge status={h.declared_status} />;
  return (
    <select
      className="input-base text-xs py-1 w-auto font-semibold"
      style={{ color: WPW_DECLARED_STATUS_COLOR[h.declared_status] }}
      value={h.declared_status}
      disabled={busy}
      onChange={(e) => onChange(e.target.value)}
    >
      {h.declared_status === "not_started" && (
        <option value="not_started" disabled>
          Nie rozpoczęto
        </option>
      )}
      {WPW_SELECTABLE_STATUSES.map((value) => (
        <option key={value} value={value}>
          {WPW_DECLARED_STATUS_LABEL[value]}
          {WPW_ACQUISITION_STATUSES.includes(value) ? " 📎" : ""}
        </option>
      ))}
    </select>
  );
}

function FilesCell({
  h,
  busy,
  canWrite,
  onUpload,
  onRemove,
  onShowHistory,
  compact,
  canConfirm,
  onUploadAgreement,
  onAnalyze,
  aiRunning,
  hideLabel,
  historyOpen,
}: {
  h: WpwHandover;
  busy: boolean;
  canWrite: boolean;
  onUpload: (f: File) => void;
  onRemove: (f: WpwHandoverFile) => void;
  /** Skrót do historii WPW (tam są też pliki) — bez niego przycisku „historia" nie ma. */
  onShowHistory?: () => void;
  compact?: boolean;
  /** Nagłówek „Pliki:" pomijamy, gdy kontrolka siedzi w rozwijanej sekcji o tej nazwie. */
  hideLabel?: boolean;
  /** Stan rozwinięcia historii WPW — przycisk pokazuje go i ZWIJA po ponownym kliku. */
  historyOpen?: boolean;
  canConfirm: boolean;
  onUploadAgreement: (f: File) => void;
  onAnalyze?: () => void;
  aiRunning?: boolean;
}) {
  // Usunięte pliki znikają z listy aktywnych, ale zostają w historii WPW (wpis „Usunięto plik").
  const files = h.attachments.filter((f) => f.state !== "removed");
  const historyBtn = onShowHistory && h.timeline.length > 0 && (
    <button
      type="button"
      onClick={onShowHistory}
      aria-expanded={historyOpen}
      className={`inline-flex items-center gap-1 hover:text-[var(--color-primary)] hover:underline shrink-0 ${
        historyOpen ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"
      }`}
      title={
        historyOpen
          ? "Zwiń historię WPW"
          : "Pokaż historię WPW — pliki, statusy, komentarze i decyzje GK"
      }
    >
      <History size={compact ? 12 : 11} />
      {!compact && " historia WPW"}
      {!compact && historyOpen !== undefined && (
        historyOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />
      )}
    </button>
  );
  const addBtn = canWrite && (
    <label
      className={`inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline shrink-0 ${
        busy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      }`}
      title="Dodaj plik — skan zgody / decyzji"
    >
      <Upload size={compact ? 13 : 12} />
      {!compact && " Dodaj plik"}
      <input
        type="file"
        className="hidden"
        disabled={busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onUpload(f);
        }}
      />
    </label>
  );
  // Porozumienie wgrywa się osobnym przyciskiem — trafia do per-plikowej decyzji GK i zapala
  // chip „porozumienie do zatwierdzenia" na działce w projekcie.
  const addAgreementBtn = canWrite && (
    <label
      className={`inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline shrink-0 ${
        busy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      }`}
      title="Dodaj porozumienie — GK zatwierdza je razem ze zgodą („Zatwierdź i przenieś”); odrzucenie zgody odrzuca też ten plik"
    >
      <FileSignature size={compact ? 13 : 12} />
      {!compact && " Dodaj porozumienie"}
      <input
        type="file"
        className="hidden"
        disabled={busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onUploadAgreement(f);
        }}
      />
    </label>
  );
  // „Analiza AI" — GK zleca przeczytanie wgranych skanów przez model; wynikowe uwagi
  // widzą obie strony (chip poniżej + lista w rozwinięciu i w sekcji zbiorczej).
  const analyzeBtn = canConfirm && onAnalyze && files.length > 0 && (
    <button
      type="button"
      disabled={busy || aiRunning}
      onClick={onAnalyze}
      className="inline-flex items-center gap-1 text-fuchsia-600 dark:text-fuchsia-400 hover:underline shrink-0 disabled:opacity-50"
      title={
        h.ai_analysis
          ? "Uruchom analizę AI ponownie (obejmie także pliki wgrane po ostatniej analizie)"
          : "Analiza AI wgranych dokumentów — wypisze braki, niezgodności i nieprawidłowości"
      }
    >
      {aiRunning ? (
        <Spinner size="sm" className="w-3 h-3" />
      ) : (
        <Sparkles size={compact ? 13 : 12} />
      )}
      {!compact &&
        (aiRunning
          ? " Analizuję…"
          : h.ai_analysis
            ? " Analiza AI ponownie"
            : " Analiza AI")}
    </button>
  );
  const aiChip = h.ai_analysis ? <AiStatusChip a={h.ai_analysis} /> : null;
  // Odznaka przy porozumieniu: rodzaj dokumentu + czy GK już je zatwierdziło (razem ze zgodą).
  // Odrzucenie jest decyzją o CAŁEJ zgodzie i widać je w pasku decyzji, więc przy plikach
  // nie ma osobnego „odrzucone" — odrzucone porozumienie nosi tylko neutralny znacznik rodzaju.
  const AGREEMENT_BADGE: Record<string, [string, string]> = {
    pending: ["porozumienie do zatwierdzenia", "border-amber-400 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"],
    approved: ["porozumienie zatwierdzone", "border-green-500 bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300"],
    rejected: ["porozumienie", "border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]"],
  };
  const agreementMark = (f: WpwHandoverFile) => {
    if (f.doc_kind !== "porozumienie" || !AGREEMENT_BADGE[f.review_status]) return null;
    const [label, cls] = AGREEMENT_BADGE[f.review_status];
    const detail =
      f.review_status === "approved"
        ? `Porozumienie zatwierdzone razem ze zgodą — ${f.reviewed_by_name || "—"} · ${fmt(f.reviewed_at)}`
        : "Porozumienie — zatwierdza się razem ze zgodą („Zatwierdź i przenieś”); odrzucenie zgody obejmuje też ten plik.";
    return (
      <span
        className={`inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border shrink-0 whitespace-nowrap ${cls}`}
        title={detail}
      >
        <FileSignature size={9} /> {label}
      </span>
    );
  };
  // Brak akcji przy pliku: porozumienie zatwierdza się RAZEM ze zgodą („Zatwierdź i przenieś"
  // niżej), a odrzucenie zgody odrzuca też jej pliki — decyzja jest jedna, o całej zgodzie.
  // Usuwać może GK (zawsze) lub negocjator — ten drugi tylko dopóki GK nie zatwierdził
  // pliku. Binarka nie zostaje: w historii WPW widnieje sam wpis (nazwa, kto, kiedy).
  const removeBtn = (f: WpwHandoverFile) =>
    (canConfirm || (canWrite && f.review_status !== "approved")) && (
      <button
        type="button"
        disabled={busy}
        onClick={() => onRemove(f)}
        className="text-[var(--color-text-muted)] hover:text-red-500 disabled:opacity-50 shrink-0"
        title={`Usuń plik „${f.original_name}” trwale (w historii zostanie tylko wpis)`}
      >
        <Trash2 size={compact ? 11 : 12} />
      </button>
    );

  if (compact) {
    // icons only — names live in the expandable detail row
    return (
      <div className="flex items-center gap-1.5 text-xs">
        {files.length === 0 && (
          <span className="text-[var(--color-text-muted)]">—</span>
        )}
        {files.map((f) => (
          <span key={f.id} className="inline-flex items-center gap-0.5">
            {f.url ? (
              <a
                href={f.url}
                target="_blank"
                rel="noreferrer"
                download={f.original_name}
                className="text-[var(--color-primary)] hover:underline"
                title={f.original_name}
              >
                <Download size={13} />
              </a>
            ) : (
              <Paperclip
                size={13}
                className="text-[var(--color-text-muted)]"
              />
            )}
            {agreementMark(f)}
            {removeBtn(f)}
          </span>
        ))}
        <OptionBar items={[addBtn, addAgreementBtn, analyzeBtn, historyBtn]} />
        {aiChip}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap text-xs min-w-0">
      {!hideLabel && (
        <span className="text-[var(--color-text-muted)] inline-flex items-center gap-1">
          <Paperclip size={12} /> Pliki:
        </span>
      )}
      {files.length === 0 && (
        <span className="text-[var(--color-text-muted)] italic px-2 first:pl-0">brak</span>
      )}
      {files.map((f) => (
        <span key={f.id} className="inline-flex items-center gap-1 min-w-0">
          {f.url ? (
            <a
              href={f.url}
              target="_blank"
              rel="noreferrer"
              download={f.original_name}
              className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline max-w-[16rem] truncate"
              title={f.original_name}
            >
              <Download size={12} className="shrink-0" />{" "}
              <span className="truncate">{f.original_name}</span>
            </a>
          ) : (
            <span className="truncate" title={f.original_name}>
              {f.original_name}
            </span>
          )}
          {agreementMark(f)}
          {removeBtn(f)}
        </span>
      ))}
      {aiChip}
      <OptionBar items={[addBtn, addAgreementBtn, analyzeBtn, historyBtn]} />
    </div>
  );
}

// Kontrolka plików negocjatora do osadzenia POZA panelem (np. sekcja WPW w szczegółach
// działki) — dokładnie ta sama FilesCell co w zamówieniu, bez akcji GK przy pliku (decyzja
// o zgodzie obejmuje jej pliki). Uprawnienia jak w zamówieniu: wgrywa negocjator, decyduje GK.
export function WpwFilesInline({
  h,
  onShowHistory,
  compact = true,
}: {
  h: WpwHandover;
  onShowHistory?: () => void;
  compact?: boolean;
}) {
  const a = useHandoverActions(h);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <FilesCell
        h={h}
        busy={a.busy}
        canWrite={a.canWrite}
        canConfirm={a.canConfirm}
        onUpload={a.uploadFile}
        onUploadAgreement={a.uploadAgreement}
        onRemove={a.removeFile}
        onAnalyze={a.runAi}
        aiRunning={a.aiRunning}
        onShowHistory={onShowHistory}
        compact={compact}
      />
      {(a.error || a.aiError) && (
        <span className="text-red-500 text-[10px]">{a.error || a.aiError}</span>
      )}
    </span>
  );
}

// GK→podwykonawca input documents for this WPW (mandatory at order time, updatable after a route
// change). On the contractor side a "Zaktualizowana" badge lights up until they mark it seen; the
// internal GK side can push an update (notifies the contractor).
function GkDocsCell({
  h,
  m,
  busy,
  canWrite,
  canConfirm,
  hideLabel,
}: {
  h: WpwHandover;
  m: Mutations;
  busy: boolean;
  canWrite: boolean;
  canConfirm: boolean;
  /** Jak w FilesCell — nagłówek zostaje na rozwijanej sekcji, nie w jej środku. */
  hideLabel?: boolean;
}) {
  const docs = h.gk_docs;
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs min-w-0">
      {!hideLabel && (
        <span className="text-[var(--color-text-muted)] inline-flex items-center gap-1">
          <Paperclip size={12} /> Od GK:
        </span>
      )}
      {docs.length === 0 && (
        <span className="text-[var(--color-text-muted)] italic px-2 first:pl-0">brak</span>
      )}
      {docs.map((f) => (
        <span key={f.id} className="inline-flex items-center gap-1 min-w-0">
          {f.url ? (
            <a
              href={f.url}
              target="_blank"
              rel="noreferrer"
              download={f.original_filename}
              className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline max-w-[16rem] truncate"
              title={f.original_filename}
            >
              <Download size={12} className="shrink-0" />{" "}
              <span className="truncate">{f.original_filename}</span>
            </a>
          ) : (
            <span className="truncate" title={f.original_filename}>
              {f.original_filename}
            </span>
          )}
          {/* GK usuwa nieaktualny plik pakietu (np. sprzed aktualizacji), żeby negocjator
              nie zebrał podpisów na złym druku; w osi czasu WPW zostaje wpis o usunięciu. */}
          {canConfirm && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (
                  window.confirm(
                    `Usunąć plik pakietu „${f.original_filename}”? Podwykonawca przestanie ` +
                      `go widzieć; w osi czasu WPW pozostanie wpis o usunięciu.`,
                  )
                )
                  m.removeGkDoc.mutate({ id: h.id, fileId: f.id });
              }}
              className="text-[var(--color-text-muted)] hover:text-red-500 disabled:opacity-50 shrink-0"
              title={`Usuń plik pakietu „${f.original_filename}”`}
            >
              <Trash2 size={11} />
            </button>
          )}
        </span>
      ))}
      {h.gk_docs_updated && (
        <span className="px-1 py-px rounded text-[9px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          ZAKTUALIZOWANA
        </span>
      )}
      <OptionBar
        items={[
          canWrite && h.gk_docs_updated && (
            <button
              type="button"
              disabled={busy}
              className="text-[var(--color-primary)] hover:underline shrink-0"
              onClick={() => m.markGkDocsSeen.mutate({ id: h.id })}
            >
              Oznacz jako przejrzane
            </button>
          ),
          canConfirm && (
            <label
              className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline cursor-pointer shrink-0"
              title="Aktualizuj załączniki GK — powiadomi podwykonawcę"
            >
              <Upload size={12} /> Aktualizuj
              <input
                type="file"
                multiple
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                  const fs = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  if (fs.length) m.updateGkDocs.mutate({ id: h.id, files: fs });
                }}
              />
            </label>
          ),
        ]}
      />
    </div>
  );
}

function GkDecision({
  h,
  busy,
  m,
  canConfirm,
  canWrite,
  latestStatusEvent,
  isAcquisition,
  conf,
  isConfirmed,
  compact,
}: {
  h: WpwHandover;
  busy: boolean;
  m: Mutations;
  canConfirm: boolean;
  /** Strona negocjatora — po odrzuceniu dostaje podpowiedź, co ma zrobić dalej. */
  canWrite?: boolean;
  latestStatusEvent: WpwHandoverEvent | undefined;
  isAcquisition: boolean;
  conf: WpwHandover["current_confirmation"];
  isConfirmed: boolean;
  compact?: boolean;
}) {
  const isRejected = conf?.decision === "rejected";
  // „Zebrano w terenie": podpisy są, skanu jeszcze nie ma — GK nie ma czego zatwierdzać, a
  // negocjator ma wgrać plik (backend sam przestawi wtedy status na „Pozyskano zgodę pisemną").
  const awaitingFile = h.declared_status === WPW_AWAITING_FILE_STATUS;
  // Odrzucenie zawsze z powodem — negocjator dostaje go w powiadomieniu i w osi czasu, bo musi
  // wgrać nowy dokument (backend odrzuca puste uzasadnienie). Tylko na zgodzie CZEKAJĄCEJ na
  // decyzję: po zatwierdzeniu jest wyłącznie „Cofnij zatwierdzenie", po odrzuceniu — nic.
  function reject() {
    if (!latestStatusEvent) return;
    const answer = window.prompt("Powód odrzucenia zgody (negocjator musi wgrać nowy dokument):");
    if (answer === null) return;
    const note = answer.trim();
    if (!note) return;
    m.confirm.mutate({
      id: h.id,
      targetEvent: latestStatusEvent.id,
      decision: "rejected",
      note,
    });
  }
  const rejectBtn = (
    <button
      type="button"
      disabled={busy}
      onClick={reject}
      className="inline-flex items-center gap-1 text-red-500 hover:underline disabled:opacity-50 px-2 first:pl-0 last:pr-0"
      title="Odrzuć zgodę — negocjator musi wgrać nowy dokument"
    >
      <X size={13} />
      {!compact && " Odrzuć"}
    </button>
  );
  return (
    <div
      className={`flex items-center text-xs divide-x divide-[var(--color-border)] ${
        compact ? "flex-wrap" : "ml-auto"
      }`}
    >
      {conf && (
        <span
          title={conf.note || undefined}
          className={`inline-flex items-center gap-1 font-medium px-2 py-0.5 rounded first:!pl-0 ${
            conf.decision === "confirmed"
              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              : conf.decision === "rejected"
                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
          }`}
        >
          <ShieldCheck size={12} />
          {compact ? "" : ` ${WPW_DECISION_LABEL[conf.decision]}`}
        </span>
      )}
      {/* Powód odrzucenia na wierzchu — to instrukcja dla negocjatora, nie tylko dymek. */}
      {!compact && isRejected && conf?.note && (
        <span className="text-red-600 dark:text-red-400 px-2" title="Powód odrzucenia">
          {conf.note}
        </span>
      )}
      {!compact && isRejected && canWrite && !canConfirm && (
        <span className="text-[var(--color-text-muted)] italic px-2">
          Wgraj nowy dokument — zgoda wróci do decyzji GK.
        </span>
      )}
      {awaitingFile && canWrite && !canConfirm && (
        <span
          className="text-sky-700 dark:text-sky-400 italic px-2 first:pl-0"
          title="Po wgraniu pliku status sam zmieni się na „Pozyskano zgodę pisemną” i trafi do akceptacji GK"
        >
          {compact
            ? "wgraj skan zgody"
            : "Wgraj skan zgody — status zmieni się na „Pozyskano zgodę pisemną” i trafi do akceptacji GK."}
        </span>
      )}
      {!compact && isRejected && canConfirm && (
        <span
          className="text-[var(--color-text-muted)] italic px-2"
          title="Ponowne odrzucenie nie ma sensu — negocjator ma wgrać nowy dokument, wtedy zgoda wróci do decyzji."
        >
          czeka na nowy dokument od negocjatora
        </span>
      )}

      {canConfirm &&
        latestStatusEvent &&
        (isConfirmed ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                m.unconfirm.mutate({
                  id: h.id,
                  targetEvent: latestStatusEvent.id,
                })
              }
              className="inline-flex items-center gap-1 text-gray-500 hover:underline disabled:opacity-50 px-2 first:pl-0 last:pr-0"
              title="Cofnij zatwierdzenie"
            >
              <Undo2 size={13} />
              {!compact && " Cofnij zatwierdzenie"}
            </button>
          </>
        ) : isAcquisition ? (
          // Acceptance is allowed ONLY on a collected written consent / art. 124 decision; on any
          // other status there is nothing to accept (and the backend rejects a confirm anyway).
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                m.confirm.mutate({
                  id: h.id,
                  targetEvent: latestStatusEvent.id,
                  decision: "confirmed",
                })
              }
              className="inline-flex items-center gap-1 text-green-600 font-medium hover:underline disabled:opacity-50 px-2 first:pl-0 last:pr-0"
              title="Zatwierdź i przenieś do tytułów dostępu właściciela (zatwierdza też wgrane porozumienie)"
            >
              <ShieldCheck size={13} />
              {!compact && " Zatwierdź i przenieś"}
            </button>
            {/* Po odrzuceniu piłka jest u negocjatora — drugie „Odrzuć" tej samej zgody
                nic nie wnosi (backend też je odrzuca); zatwierdzić nadal można. */}
            {!isRejected && rejectBtn}
          </>
        ) : awaitingFile ? (
          <span
            className="text-sky-700 dark:text-sky-400 italic px-2 first:pl-0"
            title="Negocjator zebrał podpisy w terenie — akceptacja lub odrzucenie po wgraniu skanu zgody"
          >
            {compact ? "czeka na plik" : "Zebrano w terenie — czeka na plik od negocjatora"}
          </span>
        ) : !compact ? (
          <span
            className="text-[var(--color-text-muted)] italic px-2 first:pl-0"
            title="Zatwierdzenie GK wymaga pisemnej zgody lub decyzji art. 124 UGN"
          >
            Zatwierdzenie po pozyskaniu zgody / decyzji art. 124
          </span>
        ) : null)}
    </div>
  );
}

// ── Historia WPW ──────────────────────────────────────────────────────────────
// JEDNA oś czasu WPW: statusy, komentarze, pliki negocjatora (dodane / usunięte — dawna
// osobna „Historia plików") i decyzje GK. Decyzja o zgodzie niesie pod sobą pliki, które
// objęła: backend zapisuje wpis per plik (attachment + target_event = ta decyzja), więc
// widać, KTÓRE dokumenty GK odrzucił albo przyjął. Starsze wpisy per plik (bez rodzica)
// zostają osobnymi pozycjami.
function splitTimeline(timeline: WpwHandoverEvent[]) {
  const byId = new Map(timeline.map((e) => [e.id, e]));
  const filesOf = new Map<string, WpwHandoverEvent[]>();
  const entries: WpwHandoverEvent[] = [];
  for (const ev of timeline) {
    const parent =
      ev.kind === "confirmation" && ev.attachment && ev.target_event
        ? byId.get(ev.target_event)
        : undefined;
    if (parent?.kind === "confirmation") {
      filesOf.set(parent.id, [...(filesOf.get(parent.id) ?? []), ev]);
    } else {
      entries.push(ev);
    }
  }
  return { entries, filesOf };
}

const DECISION_TONE: Record<WpwDecision, string> = {
  confirmed: "text-green-700 dark:text-green-400",
  rejected: "text-red-600 dark:text-red-400",
  needs_info: "text-amber-700 dark:text-amber-400",
  revoked: "text-[var(--color-text-muted)]",
};

// Plik wskazany we wpisie historii: link, dopóki binarka jest (usunięcie ją kasuje — wtedy
// sama przekreślona nazwa), plus znacznik porozumienia.
function FileRef({
  id,
  name,
  files,
  showRemoved = true,
}: {
  id: string | null;
  name: string | null;
  files: Map<string, WpwHandoverFile>;
  showRemoved?: boolean;
}) {
  const f = id ? files.get(id) : undefined;
  const label = name || f?.original_name || "plik";
  const removed = f?.state === "removed";
  return (
    <span className="inline-flex items-center gap-1 min-w-0 max-w-full align-bottom">
      {f?.url && !removed ? (
        <a
          href={f.url}
          target="_blank"
          rel="noreferrer"
          download={label}
          className="text-[var(--color-primary)] hover:underline truncate"
          title={label}
        >
          {label}
        </a>
      ) : (
        <span
          className={`truncate ${removed ? "line-through text-[var(--color-text-muted)]" : ""}`}
          title={label}
        >
          {label}
        </span>
      )}
      {f?.doc_kind === "porozumienie" && (
        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border border-[var(--color-border)] text-[var(--color-text-muted)] shrink-0 whitespace-nowrap">
          <FileSignature size={9} /> porozumienie
        </span>
      )}
      {removed && showRemoved && (
        <span className="px-1 py-px rounded text-[9px] font-semibold uppercase bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 shrink-0">
          Usunięty
        </span>
      )}
    </span>
  );
}

function HistoryEntry({
  ev,
  fileEvents,
  files,
}: {
  ev: WpwHandoverEvent;
  fileEvents?: WpwHandoverEvent[];
  files: Map<string, WpwHandoverFile>;
}) {
  let icon: ReactNode;
  let content: ReactNode;
  if (ev.kind === "file_added" || ev.kind === "file_removed") {
    icon = <Paperclip size={11} className="mt-0.5 text-[var(--color-text-muted)] shrink-0" />;
    content = (
      <>
        {ev.kind === "file_added" ? "Dodano plik: " : "Usunięto plik: "}
        <FileRef
          id={ev.attachment}
          name={ev.attachment_name}
          files={files}
          showRemoved={ev.kind === "file_added"}
        />
        {ev.body && <span className="text-[var(--color-text-muted)]"> — {ev.body}</span>}
      </>
    );
  } else if (ev.kind === "confirmation") {
    const tone = ev.decision ? DECISION_TONE[ev.decision] : "text-[var(--color-text-muted)]";
    icon = <ShieldCheck size={11} className={`mt-0.5 shrink-0 ${tone}`} />;
    content = (
      <>
        <span className={`font-medium ${tone}`}>
          {ev.decision ? WPW_DECISION_LABEL[ev.decision] : ev.kind_display}
        </span>
        {ev.attachment && (
          <>
            {": "}
            <FileRef id={ev.attachment} name={ev.attachment_name} files={files} />
          </>
        )}
        {ev.body && ` — ${ev.body}`}
        {fileEvents && fileEvents.length > 0 && (
          <ul
            className="mt-0.5 space-y-0.5 border-l border-[var(--color-border-muted)] pl-2"
            title="Pliki objęte tą decyzją"
          >
            {fileEvents.map((fe) => (
              <li key={fe.id} className="flex items-center gap-1 min-w-0">
                <Paperclip size={10} className={`shrink-0 ${tone}`} />
                <FileRef id={fe.attachment} name={fe.attachment_name} files={files} />
              </li>
            ))}
          </ul>
        )}
      </>
    );
  } else {
    const color =
      ev.kind === "status_change"
        ? WPW_DECLARED_STATUS_COLOR[ev.to_status as WpwDeclaredStatus]
        : undefined;
    icon = (
      <CircleDot
        size={11}
        className="mt-0.5 text-[var(--color-text-muted)] shrink-0"
        style={color ? { color } : undefined}
      />
    );
    content = eventLine(ev);
  }
  return (
    <li className="flex items-start gap-2 text-xs">
      {icon}
      <div className="min-w-0">
        <div className="text-[var(--color-text)] break-words">{content}</div>
        <div className="flex items-center gap-1.5 text-[10px] text-[var(--color-text-muted)]">
          <SideBadge side={ev.author_side} />
          <span>{ev.author_name}</span>
          <span>· {fmt(ev.changed_at)}</span>
        </div>
      </div>
    </li>
  );
}

function WpwHistory({
  h,
  busy,
  m,
  comment,
  setComment,
}: {
  h: WpwHandover;
  busy: boolean;
  m: Mutations;
  comment: string;
  setComment: (v: string) => void;
}) {
  const { entries, filesOf } = useMemo(() => splitTimeline(h.timeline), [h.timeline]);
  const files = useMemo(
    () => new Map(h.attachments.map((f) => [f.id, f])),
    [h.attachments],
  );
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <textarea
          className="input-base text-xs flex-1 min-h-[2.25rem]"
          rows={1}
          placeholder="Dodaj komentarz…"
          value={comment}
          disabled={busy}
          onChange={(e) => setComment(e.target.value)}
        />
        <button
          type="button"
          disabled={busy || !comment.trim()}
          onClick={() =>
            m.addComment.mutate(
              { id: h.id, body: comment },
              { onSuccess: () => setComment("") },
            )
          }
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
        >
          <MessageSquarePlus size={13} /> Dodaj
        </button>
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">Brak wpisów.</p>
      ) : (
        <ul className="space-y-1.5">
          {[...entries].reverse().map((ev) => (
            <HistoryEntry key={ev.id} ev={ev} fileEvents={filesOf.get(ev.id)} files={files} />
          ))}
        </ul>
      )}
    </div>
  );
}

// Nagłówek zwiniętej historii: ile wpisów i kiedy ostatni — bez rozwijania widać, że coś się
// ruszyło.
function historyCount(h: WpwHandover): number {
  return splitTimeline(h.timeline).entries.length;
}
function historySummary(h: WpwHandover): ReactNode {
  const last = h.timeline[h.timeline.length - 1];
  if (!last) return null;
  return <span>· ostatni wpis {fmt(last.changed_at)}</span>;
}

// Co ma stać w NAGŁÓWKU zwiniętej sekcji plików: znacznik analizy AI i licznik
// porozumień czekających na decyzję GK — żeby zwinięcie nie schowało pracy do zrobienia.
function filesSummary(h: WpwHandover): ReactNode {
  const pending = h.attachments.filter(
    (f) => f.state !== "removed" && f.doc_kind === "porozumienie" && f.review_status === "pending",
  ).length;
  return (
    <>
      {h.ai_analysis && <AiStatusChip a={h.ai_analysis} />}
      {pending > 0 && (
        <span className="text-[9px] uppercase tracking-wide px-1 py-px border border-amber-400 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 whitespace-nowrap">
          {pending} porozumieni(a) do decyzji
        </span>
      )}
    </>
  );
}

function gkSummary(h: WpwHandover): ReactNode {
  if (!h.gk_docs_updated) return null;
  return (
    <span
      className="px-1 py-px rounded text-[9px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
      title="GK zaktualizował załączniki zlecenia"
    >
      ZAKTUALIZOWANA
    </span>
  );
}

// One WPW = one owner of a parcel. Primary actions (download, set status, confirm/undo) are
// inline; the chevron expands the WPW history (comments, files, GK decisions — one timeline).
// WPW „nie ma w żadnym zamówieniu" — wniosek o zmianę usunął je z ostatniego zamówienia.
// Stoi w miejscu podwykonawcy i numeru zamówienia; po dodaniu do zamówienia znika sam.
function DetachedWarning({ h, compact }: { h: WpwHandover; compact?: boolean }) {
  const d = h.detached;
  const from = d?.from_order_number
    ? `Usunięto z zamówienia ${d.from_order_number}` +
      (d.from_contractor ? ` (${d.from_contractor})` : "") +
      (d.at ? ` — ${fmt(d.at)}` : "") +
      (d.by ? `, ${d.by}` : "")
    : "";
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0">
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
        title={from || undefined}
      >
        <AlertTriangle size={11} className="shrink-0" />
        UWAGA: nie ma w żadnym zamówieniu
      </span>
      {!compact && from && <span className="text-[10px] text-[var(--color-text-muted)]">{from}</span>}
    </span>
  );
}

// Koordynator rozstrzyga WPW spoza zamówień sam: plik zgody (uzgodnienie → tytuł „Pozyskany")
// albo odmowy (tytuł „Odmowa") — bez akceptacji, bo nie ma negocjatora, którego trzeba by
// sprawdzać. Backend odmawia tego dla WPW w zamówieniu.
function ManualOutcomeForm({ h }: { h: WpwHandover }) {
  const m = useWpwHandoverMutations();
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<"consent" | "refusal">("consent");
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
      >
        <Upload size={11} /> Wgraj zgodę lub odmowę (koordynator, bez akceptacji)
      </button>
    );
  }
  const submit = () => {
    setErr(null);
    if (!files.length) {
      setErr("Dołącz plik ze zgodą albo z odmową.");
      return;
    }
    m.manualOutcome.mutate(
      { id: h.id, outcome, files, note },
      {
        onSuccess: () => {
          setFiles([]);
          setNote("");
          setOpen(false);
        },
        onError: (e) => setErr(extractError(e)),
      },
    );
  };
  return (
    <div className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-2 space-y-1.5 text-xs">
      <div className="font-medium text-[var(--color-text)]">Dokument od koordynatora — bez akceptacji</div>
      <p className="text-[11px] text-[var(--color-text-muted)]">
        Tego WPW nie ma w żadnym zamówieniu, więc nie ma negocjatora. Uzgodnienie od razu
        zabezpiecza tytuł właściciela („Pozyskany”), odmowa ustawia tytuł „Odmowa”.
      </p>
      <div className="flex flex-wrap gap-3">
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={outcome === "consent"} onChange={() => setOutcome("consent")} />
          Uzgodnienie (zgoda)
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={outcome === "refusal"} onChange={() => setOutcome("refusal")} />
          Odmowa
        </label>
      </div>
      <input
        type="file"
        multiple
        aria-label="Plik zgody lub odmowy"
        onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
        className="block text-[11px]"
      />
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Uwagi (opcjonalnie)"
        className="w-full border border-[var(--color-border)] rounded px-2 py-1 text-xs bg-[var(--color-bg-surface)]"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={m.manualOutcome.isPending}
          className="px-2 py-1 rounded bg-[var(--color-primary)] text-white text-[11px] font-medium disabled:opacity-50"
        >
          {m.manualOutcome.isPending ? "Zapisywanie…" : "Zapisz bez akceptacji"}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setErr(null); }}
          className="text-[11px] text-[var(--color-text-muted)] hover:underline"
        >
          Anuluj
        </button>
      </div>
      {err && <p className="text-red-500 text-[11px]">{err}</p>}
    </div>
  );
}

// Uwagi AI dotyczą plików negocjatora, więc siedzą w ich sekcji — pod przyciskiem „Analiza AI".
const AI_FINDINGS_HEADING = (
  <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
    Analiza AI dokumentów
  </div>
);

// WPW wskazane w adresie (?handover=<id>) — wejście z powiadomienia „WPW / Porozumienie do
// zatwierdzenia": przewiń do niego, gdy panele nad nim się już ułożą. Pauza, bo sekcje nad
// panelem (wnioski o zmianę, pozycje) doładowują się osobno i przesuwają stronę.
function useScrollToFocused<T extends HTMLElement>(focused: boolean) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!focused) return;
    const timer = setTimeout(() => {
      if (typeof ref.current?.scrollIntoView === "function") {
        ref.current.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [focused]);
  return ref;
}

function HandoverCard({ h, focused = false }: { h: WpwHandover; focused?: boolean }) {
  // Pliki negocjatora i pakiet od GK to DWA różne zbiory dokumentów — każdy w swojej
  // rozwijanej sekcji, żeby nie czytać ich jako jednego ciągu ikon.
  // WPW spoza zamówień: „Pliki" od razu otwarte — tam koordynator wgrywa zgodę/odmowę.
  // WPW wskazane z powiadomienia też — tam są przyciski zatwierdzenia porozumienia.
  const [filesOpen, setFilesOpen] = useState(!h.order || focused);
  const [gkOpen, setGkOpen] = useState(false);
  // Historia WPW (statusy, komentarze, pliki, decyzje GK) — trzecia rozwijana sekcja; strzałka
  // w nagłówku karty rozwija ją tak samo jak klik w jej nagłówek.
  const [historyOpen, setHistoryOpen] = useState(false);
  const a = useHandoverActions(h);
  const rootRef = useScrollToFocused<HTMLDivElement>(focused);

  return (
    <div
      ref={rootRef}
      data-focused={focused || undefined}
      className={`card overflow-hidden${focused ? " border-[var(--color-primary)] shadow-hard-red" : ""}`}
    >
      {/* main line: WPW (owner) + działka | status */}
      <div className="flex items-start gap-3 p-3">
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          aria-expanded={historyOpen}
          title={historyOpen ? "Zwiń historię WPW" : "Rozwiń historię WPW"}
          className="mt-0.5 text-[var(--color-text-muted)] shrink-0"
        >
          {historyOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="text-[9px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
            WPW — właściciel
          </div>
          <div className="flex items-center gap-1.5 font-semibold text-sm text-[var(--color-text)]">
            <UserIcon
              size={13}
              className="text-[var(--color-text-muted)] shrink-0"
            />
            <span className="truncate">{h.owner.full_name || "—"}</span>
            {h.owner.outdated_at && (
              <span
                className="px-1 py-px rounded text-[8px] font-semibold bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 shrink-0"
                title={h.owner.outdated_reason || "Właściciel nieaktualny — ponowny wypis go nie potwierdził."}
              >
                NIEAKTUALNY
              </span>
            )}
            <span className="text-[10px] font-normal text-[var(--color-text-muted)]">
              {h.owner.kind_display}
            </span>
          </div>
          <div
            className="text-[11px] text-[var(--color-text-muted)] font-mono truncate"
            title={h.parcel.teryt}
          >
            dz. {h.parcel.teryt}
          </div>
          {/* contractor + order number — disambiguate the same owner+parcel recurring across orders */}
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[var(--color-text-muted)]">
            {!h.order && <DetachedWarning h={h} />}
            {h.order?.contractor && (
              <span className="inline-flex items-center gap-1 min-w-0">
                <Briefcase size={11} className="shrink-0" />
                <span className="truncate">{h.order.contractor}</span>
              </span>
            )}
            {h.order?.order_number && (
              <Link
                to={`/orders/${h.order.id}`}
                className="inline-flex items-center gap-1 font-mono text-[var(--color-primary)] hover:underline"
                title="Otwórz zamówienie"
              >
                {h.order.order_number}
              </Link>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="text-[9px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
            Status
          </div>
          <StatusControl
            h={h}
            busy={a.busy}
            canWrite={a.canWrite}
            onChange={a.onStatusChange}
          />
          <SupersededLine h={h} />
          <ProblemReasonLine h={h} latestStatusEvent={a.latestStatusEvent} />
        </div>
      </div>
      <ProblemReasonModal a={a} h={h} />

      {/* dokumenty: dwie OSOBNE sekcje rozwijane — od negocjatora i od GK */}
      <DisclosureSection
        open={filesOpen}
        onToggle={() => setFilesOpen((v) => !v)}
        icon={<Paperclip size={12} className="shrink-0" />}
        label={h.order ? "Pliki od negocjatora" : "Pliki"}
        count={activeFileCount(h)}
        summary={filesSummary(h)}
      >
        <div className="space-y-2">
          {!h.order && a.canConfirm && <ManualOutcomeForm h={h} />}
          <FilesCell
            h={h}
            busy={a.busy}
            canWrite={a.canWrite}
            canConfirm={a.canConfirm}
            onUpload={a.uploadFile}
            onUploadAgreement={a.uploadAgreement}
            onRemove={a.removeFile}
            onAnalyze={a.runAi}
            aiRunning={a.aiRunning}
            hideLabel
          />
          {h.ai_analysis && <AiFindings a={h.ai_analysis} heading={AI_FINDINGS_HEADING} />}
        </div>
      </DisclosureSection>

      <DisclosureSection
        open={gkOpen}
        onToggle={() => setGkOpen((v) => !v)}
        icon={<Briefcase size={12} className="shrink-0" />}
        label="Dokumenty od GK (pakiet w teren)"
        count={h.gk_docs.length}
        summary={gkSummary(h)}
      >
        <GkDocsCell
          h={h}
          m={a.m}
          busy={a.busy}
          canWrite={a.canWrite}
          canConfirm={a.canConfirm}
          hideLabel
        />
      </DisclosureSection>

      {/* decyzja GK zostaje na wierzchu — to jedyna akcja, która kończy sprawę.
          Pasek pojawia się tylko wtedy, gdy ma co pokazać: u negocjatora bez decyzji
          byłby pustym paskiem z kreską. */}
      {(a.conf ||
        (a.canConfirm && a.latestStatusEvent) ||
        (a.canWrite && h.declared_status === WPW_AWAITING_FILE_STATUS) ||
        a.error ||
        a.aiError) && (
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-3 py-2 border-t border-[var(--color-border-muted)]">
        <GkDecision
          h={h}
          busy={a.busy}
          m={a.m}
          canConfirm={a.canConfirm}
          canWrite={a.canWrite}
          latestStatusEvent={a.latestStatusEvent}
          isAcquisition={a.isAcquisition}
          conf={a.conf}
          isConfirmed={a.isConfirmed}
        />
        {(a.error || a.aiError) && (
          <span className="w-full text-red-500 text-xs">{a.error || a.aiError}</span>
        )}
      </div>
      )}

      <DisclosureSection
        open={historyOpen}
        onToggle={() => setHistoryOpen((v) => !v)}
        icon={<History size={12} className="shrink-0" />}
        label="Historia WPW"
        count={historyCount(h)}
        summary={historySummary(h)}
      >
        <WpwHistory
          h={h}
          busy={a.busy}
          m={a.m}
          comment={a.comment}
          setComment={a.setComment}
        />
      </DisclosureSection>
    </div>
  );
}

// Compact table row — same controls as the card, laid out as real table cells. The chevron expands
// a full-width detail row (files, GK package, WPW history) below.
function HandoverTableRow({ h, focused = false }: { h: WpwHandover; focused?: boolean }) {
  // Wiersz wskazany z powiadomienia (?handover=) startuje rozwinięty — z plikami do decyzji.
  const [open, setOpen] = useState(focused);
  const rowRef = useScrollToFocused<HTMLTableRowElement>(focused);
  // W tabeli wiersz rozwija się już kliknięciem strzałki, więc obie sekcje dokumentów
  // startują otwarte — ale nadal ZWIJAJĄ się po kliknięciu w nagłówek. Historia WPW
  // (długa) startuje zwinięta; ikona historii w kolumnie plików rozwija wiersz razem z nią.
  const [filesOpen, setFilesOpen] = useState(true);
  const [gkOpen, setGkOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const a = useHandoverActions(h);

  return (
    <>
      <tr
        ref={rowRef}
        data-focused={focused || undefined}
        className={`border-t border-[var(--color-border-muted)] align-middle ${
          focused
            ? "bg-[var(--color-primary-dim)] outline outline-2 -outline-offset-2 outline-[var(--color-primary)]"
            : "hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
        }`}
      >
        <td className="px-3 py-2 w-px align-top">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[var(--color-text-muted)] mt-0.5"
          >
            {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </button>
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-1.5 font-medium text-[var(--color-text)]">
            <UserIcon
              size={12}
              className="text-[var(--color-text-muted)] shrink-0"
            />
            <span className="truncate max-w-[16rem]">
              {h.owner.full_name || "—"}
            </span>
            {h.owner.outdated_at && (
              <span
                className="px-1 py-px rounded text-[8px] font-semibold bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 shrink-0"
                title={h.owner.outdated_reason || "Właściciel nieaktualny — ponowny wypis go nie potwierdził."}
              >
                NIEAKTUALNY
              </span>
            )}
            {h.gk_docs_updated && (
              <span
                className="px-1 py-px rounded text-[8px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0"
                title="GK zaktualizował załączniki zlecenia"
              >
                ZAKTUALIZOWANA
              </span>
            )}
          </div>
          <div className="text-[10px] text-[var(--color-text-muted)] pl-[18px]">
            {h.owner.kind_display}
          </div>
        </td>
        <td className="px-3 py-2 text-xs text-[var(--color-text-muted)]">
          {h.order ? (
            <span
              className="inline-block truncate max-w-[14rem] align-middle"
              title={h.order.contractor || undefined}
            >
              {h.order.contractor || "—"}
            </span>
          ) : (
            <DetachedWarning h={h} compact />
          )}
        </td>
        <td
          className="px-3 py-2 w-px whitespace-nowrap font-mono text-[11px] text-[var(--color-text-muted)]"
          title={h.parcel.teryt}
        >
          {h.parcel.teryt}
        </td>
        <td className="px-3 py-2 w-px">
          <StatusControl
            h={h}
            busy={a.busy}
            canWrite={a.canWrite}
            onChange={a.onStatusChange}
          />
          <SupersededLine h={h} compact />
          <ProblemReasonLine h={h} latestStatusEvent={a.latestStatusEvent} compact />
          <ProblemReasonModal a={a} h={h} />
        </td>
        <td className="px-3 py-2 w-px whitespace-nowrap">
          <FilesCell
            h={h}
            busy={a.busy}
            canWrite={a.canWrite}
            canConfirm={a.canConfirm}
            onUpload={a.uploadFile}
            onUploadAgreement={a.uploadAgreement}
            onRemove={a.removeFile}
            onAnalyze={a.runAi}
            aiRunning={a.aiRunning}
            onShowHistory={() => {
              const next = !(open && historyOpen);
              if (next) setOpen(true);
              setHistoryOpen(next);
            }}
            historyOpen={open && historyOpen}
            compact
          />
        </td>
        <td className="px-3 py-2 w-px whitespace-nowrap">
          <GkDecision
            h={h}
            busy={a.busy}
            m={a.m}
            canConfirm={a.canConfirm}
            canWrite={a.canWrite}
            latestStatusEvent={a.latestStatusEvent}
            isAcquisition={a.isAcquisition}
            conf={a.conf}
            isConfirmed={a.isConfirmed}
            compact
          />
        </td>
      </tr>
      {(a.error || a.aiError) && (
        <tr>
          <td colSpan={7} className="px-2 pb-1 text-red-500 text-xs">
            {a.error || a.aiError}
          </td>
        </tr>
      )}
      {open && (
        <tr className="bg-black/[0.02] dark:bg-white/[0.03]">
          <td
            colSpan={7}
            className="px-3 py-3 border-t border-[var(--color-border-muted)] space-y-3"
          >
            <div className="border border-[var(--color-border-muted)] rounded overflow-hidden bg-[var(--color-bg-surface)]">
              <DisclosureSection
                open={filesOpen}
                onToggle={() => setFilesOpen((v) => !v)}
                icon={<Paperclip size={12} className="shrink-0" />}
                label={h.order ? "Pliki od negocjatora" : "Pliki"}
                count={activeFileCount(h)}
                summary={filesSummary(h)}
              >
                <div className="space-y-2">
                  {!h.order && a.canConfirm && <ManualOutcomeForm h={h} />}
                  <FilesCell
                    h={h}
                    busy={a.busy}
                    canWrite={a.canWrite}
                    canConfirm={a.canConfirm}
                    onUpload={a.uploadFile}
                    onUploadAgreement={a.uploadAgreement}
                    onRemove={a.removeFile}
                    onAnalyze={a.runAi}
                    aiRunning={a.aiRunning}
                    hideLabel
                  />
                  {h.ai_analysis && <AiFindings a={h.ai_analysis} heading={AI_FINDINGS_HEADING} />}
                </div>
              </DisclosureSection>
              <DisclosureSection
                open={gkOpen}
                onToggle={() => setGkOpen((v) => !v)}
                icon={<Briefcase size={12} className="shrink-0" />}
                label="Dokumenty od GK (pakiet w teren)"
                count={h.gk_docs.length}
                summary={gkSummary(h)}
              >
                <GkDocsCell
                  h={h}
                  m={a.m}
                  busy={a.busy}
                  canWrite={a.canWrite}
                  canConfirm={a.canConfirm}
                  hideLabel
                />
              </DisclosureSection>
              <DisclosureSection
                open={historyOpen}
                onToggle={() => setHistoryOpen((v) => !v)}
                icon={<History size={12} className="shrink-0" />}
                label="Historia WPW"
                count={historyCount(h)}
                summary={historySummary(h)}
              >
                <WpwHistory
                  h={h}
                  busy={a.busy}
                  m={a.m}
                  comment={a.comment}
                  setComment={a.setComment}
                />
              </DisclosureSection>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Project-level rollup: parcels that have never been sent into the field for WPW (no owner has a
// handover). Only meaningful on the workspace WPW tab (connection-scoped), shown under the main
// table as a "still to do" list.
function WpwNeverSentParcels({ connectionId }: { connectionId: string }) {
  const { data: parcels = [], isLoading } =
    useWpwNeverSentParcels(connectionId);
  if (isLoading) return null;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
          Działki WPW nigdy nie wysłane w teren
        </h3>
        <span className="text-[11px] text-[var(--color-text-muted)]">
          {parcels.length}
        </span>
      </div>
      {parcels.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">
          Wszystkie działki prywatne z wypisem dla tego projektu zostały już
          wysłane w teren (WPW) lub nie wymagają pozyskania dostępu.
        </p>
      ) : (
        <div className="overflow-x-auto border border-[var(--color-border-muted)] rounded">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] border-b border-[var(--color-border-muted)] bg-black/[0.04] dark:bg-white/[0.05]">
                <th className="px-3 py-2">Działka</th>
                <th className="px-3 py-2">Obręb</th>
                <th className="px-3 py-2 w-px whitespace-nowrap text-center">
                  Właściciele
                </th>
                <th className="px-3 py-2 w-px whitespace-nowrap">
                  Status działki
                </th>
              </tr>
            </thead>
            <tbody>
              {parcels.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-[var(--color-border-muted)] align-middle hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
                >
                  <td
                    className="px-3 py-1.5 font-mono text-[11px] text-[var(--color-text)] whitespace-nowrap"
                    title={p.teryt}
                  >
                    {p.teryt}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-[var(--color-text-muted)]">
                    {p.obreb || "—"}
                  </td>
                  <td className="px-3 py-1.5 w-px whitespace-nowrap text-xs text-[var(--color-text-muted)] text-center">
                    {p.owners_count}
                  </td>
                  <td className="px-3 py-1.5 w-px whitespace-nowrap text-xs text-[var(--color-text-muted)]">
                    {p.status_display}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface WpwHandoverPanelProps {
  /** One of orderId (order pages) or connectionId (ZE workspace rollup) is required. */
  orderId?: string;
  connectionId?: string;
  /** Optional heading; defaults to "Przekazane WPW (per właściciel)". */
  title?: string;
  /** Optional slot rendered under the header — order pages put the owners-list controls here. */
  ownersList?: ReactNode;
  /** Owners list (cards / table) scrolls in its own pane capped at 70vh, header and filters
   *  stay in place — the WPW tab of a project can hold hundreds of owners. */
  scrollList?: boolean;
}

export function WpwHandoverPanel({
  orderId,
  connectionId,
  title,
  ownersList,
  scrollList = false,
}: WpwHandoverPanelProps) {
  // „pokaż nieaktualnych właścicieli” — WPW właścicieli, których ponowny wypis nie
  // potwierdził (domyślnie schowane na backendzie; zostają w zleceniu jako historia).
  const [showOutdated, setShowOutdated] = useState(false);
  const params: Record<string, string> = orderId
    ? { order: orderId }
    : connectionId
      ? { connection: connectionId }
      : {};
  if (showOutdated) params.include_outdated = "1";
  const enabled = !!(orderId || connectionId);
  // Decyzja GK / Pliki selects are hidden on order pages (small per-order list); they stay on the
  // connection-scoped WPW tab where the dataset is large enough to need them.
  const isOrderPage = !!orderId;
  const { data: handovers = [], isLoading } = useWpwHandovers(params, enabled);
  // ?handover=<id> — wejście z powiadomienia: ta karta przewija się w pole widzenia i świeci.
  const [searchParams] = useSearchParams();
  const focusedHandoverId = searchParams.get("handover");
  // Negocjator (podwykonawca) — i użytkownik GK w „Widoku podwykonawcy" — dostaje krótką
  // instrukcję obsługi sekcji: skąd zmienić status i którędy wgrać skan zgody.
  const { isPreview } = useContractorPreview();
  const negotiatorView = isPreview || handovers.some((h) => h.can_write);

  // view mode persists across sessions (per user, per browser)
  const [view, setView] = useState<"cards" | "table">(() => {
    try {
      return localStorage.getItem(VIEW_LS_KEY) === "table" ? "table" : "cards";
    } catch {
      return "cards";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_LS_KEY, view);
    } catch {
      /* ignore */
    }
  }, [view]);

  // filters + sort
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<Set<WpwDeclaredStatus>>(
    () => new Set(),
  );
  const [decision, setDecision] = useState<DecisionFilter>("all");
  const [kind, setKind] = useState("all");
  const [filesFilter, setFilesFilter] = useState<FilesFilter>("all");
  const [parcelFilter, setParcelFilter] = useState("all");
  const [sortField, setSortField] = useState<SortField>("owner");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const kindOptions = useMemo(
    () =>
      Array.from(
        new Set(handovers.map((h) => h.owner.kind_display).filter(Boolean)),
      ).sort(),
    [handovers],
  );
  const parcelOptions = useMemo(
    () =>
      Array.from(
        new Set(handovers.map((h) => h.parcel.teryt).filter(Boolean)),
      ).sort(),
    [handovers],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return handovers.filter((h) => {
      if (s) {
        const hay =
          `${h.owner.full_name || ""} ${h.parcel.teryt || ""} ${h.parcel.short_number || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (statusFilter.size && !statusFilter.has(h.declared_status))
        return false;
      if (decision !== "all") {
        if (decision === "none") {
          if (h.current_confirmation) return false;
        } else if (h.current_confirmation?.decision !== decision) return false;
      }
      if (kind !== "all" && h.owner.kind_display !== kind) return false;
      if (filesFilter === "with" && activeFileCount(h) === 0) return false;
      if (filesFilter === "without" && activeFileCount(h) > 0) return false;
      if (parcelFilter !== "all" && h.parcel.teryt !== parcelFilter)
        return false;
      return true;
    });
  }, [handovers, q, statusFilter, decision, kind, filesFilter, parcelFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((x, y) => {
      let c = 0;
      switch (sortField) {
        case "owner":
          c = (x.owner.full_name || "").localeCompare(
            y.owner.full_name || "",
            "pl",
          );
          break;
        case "status":
          c = STATUS_ORDER[x.declared_status] - STATUS_ORDER[y.declared_status];
          break;
        case "parcel":
          c = (x.parcel.teryt || "").localeCompare(y.parcel.teryt || "", "pl");
          break;
        case "updated":
          c = lastActivity(x) - lastActivity(y);
          break;
        case "decision":
          c = decisionRank(x) - decisionRank(y);
          break;
        case "files":
          c = activeFileCount(x) - activeFileCount(y);
          break;
      }
      if (c === 0)
        c = (x.owner.full_name || "").localeCompare(
          y.owner.full_name || "",
          "pl",
        );
      return c * dir;
    });
  }, [filtered, sortField, sortDir]);

  const anyFilter =
    !!q ||
    statusFilter.size > 0 ||
    decision !== "all" ||
    kind !== "all" ||
    filesFilter !== "all" ||
    parcelFilter !== "all";
  function resetFilters() {
    setQ("");
    setStatusFilter(new Set());
    setDecision("all");
    setKind("all");
    setFilesFilter("all");
    setParcelFilter("all");
  }
  // „Pobierz ZIP" (tylko na karcie zamówienia): jeden przycisk pakuje w tle wszystkie zgody
  // wgrane przez negocjatora → task bg → auto-pobranie archiwum, gdy gotowe.
  const consentsZip = useBackgroundZip(() => startWpwHandoverZip(orderId!));
  // Mirrors the backend gate (non-removed attachments with a stored file) so the button is
  // greyed out instead of 404-ing when the negotiator hasn't uploaded anything yet.
  const hasZipFiles = handovers.some((h) =>
    h.attachments.some((f) => f.state !== "removed" && !!f.url),
  );
  // „Pakiety do druku (ZIP)" — obok wykazu właścicieli: pakiety „Od GK" wszystkich WPW
  // zamówienia naraz, do wydruku w terenie. Pobranie to odczyt, więc działa też w „Widoku
  // podwykonawcy". Pakiety nieaktualne (nowsze zlecenie) backend pomija — bramka jak tam.
  const packagesZip = useBackgroundZip(() => startWpwPackagesZip(orderId!));
  const printableCount = handovers.filter(
    (h) => !h.superseded_by && h.gk_docs.some((f) => !!f.url),
  ).length;

  // „Analiza AI" dla całego zamówienia: jedno zadanie w tle przechodzi po wszystkich
  // właścicielach z wgranymi plikami. Uruchamia GK (can_confirm); podwykonawca uwagi tylko
  // czyta — ma je w sekcji zbiorczej poniżej i przy swoim wierszu.
  const aiOrder = useAiAnalysisRun();
  const canRunAi = !isPreview && handovers.some((h) => h.can_confirm);
  const hasAiFiles = handovers.some((h) =>
    h.attachments.some((f) => f.state !== "removed"),
  );

  if (!enabled) return null;
  // Embedded on an order page: stay invisible for non-WPW orders / orders with no owners yet.
  if (orderId && (isLoading || handovers.length === 0)) return null;

  // Toolbar (filters/sort/view) only adds value with more than one row.
  const showToolbar = !isLoading && handovers.length > 1;
  const effectiveView = showToolbar ? view : "cards";

  return (
    <section className="my-6 space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-[var(--color-text)]">
          {title ?? "Przekazane WPW (per właściciel)"}
        </h2>
        <span className="text-[10px] font-bold px-1.5 py-px rounded bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white">
          DLC_01
        </span>
        {!isLoading && (
          <span className="text-xs text-[var(--color-text-muted)]">
            {anyFilter
              ? `${sorted.length} z ${handovers.length}`
              : handovers.length}
          </span>
        )}
        <label
          className="flex cursor-pointer select-none items-center gap-1.5 text-[11px] font-normal text-[var(--color-text-muted)]"
          title="Właściciele oznaczeni jako nieaktualni po ponownie zamówionym wypisie (zmiana właścicieli) — ich WPW zostają w zleceniu jako historia, domyślnie schowane."
        >
          <input
            type="checkbox"
            className="accent-[var(--color-primary)] cursor-pointer"
            checked={showOutdated}
            onChange={(e) => setShowOutdated(e.target.checked)}
          />
          pokaż nieaktualnych właścicieli
        </label>
        {isOrderPage && canRunAi && (
          <button
            type="button"
            onClick={() => aiOrder.run(() => startWpwOrderAgreementAi(orderId!))}
            disabled={aiOrder.running || !hasAiFiles}
            title={
              hasAiFiles
                ? "Przeczytaj AI wszystkie dokumenty wgrane przez podwykonawcę i wypisz braki, niezgodności i nieprawidłowości. Uwagi zobaczy także podwykonawca."
                : "Brak plików do analizy — negocjator nie wgrał jeszcze żadnych dokumentów"
            }
            className="ml-auto inline-flex items-center gap-1.5 rounded border border-fuchsia-400 dark:border-fuchsia-800 px-2 py-1 text-xs font-medium text-fuchsia-700 dark:text-fuchsia-300 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-950/40 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {aiOrder.running ? (
              <Spinner size="sm" className="w-3.5 h-3.5" />
            ) : (
              <Sparkles size={13} className="shrink-0" />
            )}
            {aiOrder.running ? "Analizuję…" : "Analiza AI porozumień"}
            <span className="text-[9px] uppercase tracking-wide opacity-70">eksperyment</span>
          </button>
        )}
        {isOrderPage && (
          <button
            type="button"
            onClick={consentsZip.start}
            disabled={consentsZip.running || isPreview || !hasZipFiles}
            title={
              hasZipFiles
                ? "Pobierz wszystkie zgody wgrane przez podwykonawcę jako jeden ZIP"
                : "Brak plików do pobrania — negocjator nie wgrał jeszcze żadnych dokumentów"
            }
            className={`${canRunAi ? "" : "ml-auto "}inline-flex items-center gap-1.5 rounded border border-[var(--color-border)] px-2 py-1 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {consentsZip.running ? (
              <Spinner size="sm" className="w-3.5 h-3.5" />
            ) : (
              <Download size={13} className="shrink-0" />
            )}
            {consentsZip.running ? "Pakowanie…" : "Pobierz ZIP"}
          </button>
        )}
      </div>
      {consentsZip.error && (
        <p className="text-xs text-red-600 dark:text-red-400">{consentsZip.error}</p>
      )}
      {aiOrder.error && (
        <p className="text-xs text-red-600 dark:text-red-400">{aiOrder.error}</p>
      )}

      {!isLoading && <AiOrderFindings handovers={handovers} />}

      {(ownersList || isOrderPage) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {ownersList}
          {isOrderPage && (
            <button
              type="button"
              onClick={packagesZip.start}
              disabled={packagesZip.running || printableCount === 0}
              title={
                printableCount > 0
                  ? `Pobierz jednym ZIP-em pakiety „Od GK” do wydruku (właścicieli: ` +
                    `${printableCount}, folder na każdego). Pakiety nieaktualne są pominięte.`
                  : "Brak pakietów do wydruku — GK nie przekazał jeszcze dokumentów do podpisu"
              }
              className="inline-flex items-center gap-1.5 rounded border border-[var(--color-border)] px-2 py-1 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {packagesZip.running ? (
                <Spinner size="sm" className="w-3.5 h-3.5" />
              ) : (
                <Printer size={13} className="shrink-0" />
              )}
              {packagesZip.running ? "Pakowanie…" : "Pakiety do druku (ZIP)"}
            </button>
          )}
          {packagesZip.error && (
            <span className="text-xs text-red-600 dark:text-red-400">{packagesZip.error}</span>
          )}
        </div>
      )}

      {negotiatorView && !isLoading && handovers.length > 0 && (
        <div className="flex items-start gap-2 rounded border border-[var(--color-border-muted)] bg-black/[0.02] dark:bg-white/[0.04] px-3 py-2 text-xs text-[var(--color-text-muted)]">
          <Info
            size={13}
            className="mt-0.5 shrink-0 text-[var(--color-primary)]"
          />
          <p className="leading-relaxed">
            Jeden wiersz = jeden właściciel jednej działki.{" "}
            <strong className="font-semibold text-[var(--color-text)]">
              Status
            </strong>{" "}
            zmieniasz listą rozwijaną po prawej stronie wiersza — statusy
            oznaczone 📎 (pozyskano zgodę pisemną / decyzję art. 124 UGN)
            wymagają dołączenia skanu.{" "}
            <strong className="font-semibold text-[var(--color-text)]">
              Skan zgody
            </strong>{" "}
            wgrywasz przyciskiem <em>„Dodaj plik”</em> w wierszu właściciela;
            omyłkowy plik usuniesz ikoną kosza — usunięcie zostaje odnotowane
            w historii plików. Strzałka{" "}
            <em>„&rsaquo;”</em> po lewej rozwija komentarze i historię — tam
            pisz do GK ELEKTIM w sprawie konkretnego właściciela. Dokumenty{" "}
            <em>„Od GK”</em> to pakiet do pozyskania podpisu; gdy zapali się
            znacznik <em>ZAKTUALIZOWANA</em>, pobierz nową wersję i oznacz ją
            jako przejrzaną. GK ELEKTIM może zlecić{" "}
            <strong className="font-semibold text-[var(--color-text)]">
              analizę AI
            </strong>{" "}
            wgranych przez Ciebie dokumentów — ewentualne uwagi zobaczysz w sekcji{" "}
            <em>„Uwagi AI do przesłanych porozumień”</em> i przy swoim wierszu.
            To podpowiedź maszyny, a nie decyzja GK: jeśli uważasz ją za błędną,
            napisz komentarz zamiast jechać w teren.
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="py-6 flex justify-center">
          <Spinner />
        </div>
      ) : handovers.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)] py-2">
          Brak właścicieli do pozyskania zgody (WPW) dla tego{" "}
          {orderId ? "zamówienia" : "projektu"}.
        </p>
      ) : (
        <>
          {showToolbar && (
            <div className="rounded border border-[var(--color-border-muted)] divide-y divide-[var(--color-border-muted)]">
              {/* FILTRY — czym zawężam listę */}
              <div className="flex items-center gap-2 flex-wrap p-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)] shrink-0">
                  <Filter size={12} /> Filtry
                </span>
                <div className="relative flex-1 min-w-[12rem]">
                  <Search
                    size={14}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
                  />
                  <input
                    className="input-base text-xs pl-7 w-full"
                    placeholder="Szukaj: właściciel / działka…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
                <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                {!isOrderPage && (
                  <select
                    className="input-base text-xs py-1 w-auto"
                    value={decision}
                    onChange={(e) =>
                      setDecision(e.target.value as DecisionFilter)
                    }
                  >
                    <option value="all">Decyzja GK: wszystkie</option>
                    <option value="confirmed">Zaakceptowano przez GK</option>
                    <option value="rejected">Odrzucono przez GK</option>
                    <option value="needs_info">Wymaga uzupełnienia</option>
                    <option value="revoked">Cofnięto zatwierdzenie</option>
                    <option value="none">Brak decyzji</option>
                  </select>
                )}
                {kindOptions.length > 1 && (
                  <select
                    className="input-base text-xs py-1 w-auto"
                    value={kind}
                    onChange={(e) => setKind(e.target.value)}
                  >
                    <option value="all">Typ właściciela: wszyscy</option>
                    {kindOptions.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                )}
                {!isOrderPage && (
                  <select
                    className="input-base text-xs py-1 w-auto"
                    value={filesFilter}
                    onChange={(e) =>
                      setFilesFilter(e.target.value as FilesFilter)
                    }
                  >
                    <option value="all">Pliki: wszystkie</option>
                    <option value="with">Z plikami</option>
                    <option value="without">Bez plików</option>
                  </select>
                )}
                {parcelOptions.length > 1 && (
                  <select
                    className="input-base text-xs py-1 w-auto"
                    value={parcelFilter}
                    onChange={(e) => setParcelFilter(e.target.value)}
                  >
                    <option value="all">Działka: wszystkie</option>
                    {parcelOptions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                )}
                {/* zawsze widoczny — użytkownik ma wiedzieć, że jest czym wyjść z filtrów */}
                <button
                  type="button"
                  onClick={resetFilters}
                  disabled={!anyFilter}
                  title={
                    anyFilter
                      ? "Zdejmij wszystkie filtry i pokaż pełną listę"
                      : "Żaden filtr nie jest ustawiony"
                  }
                  className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:underline disabled:opacity-50 disabled:no-underline shrink-0"
                >
                  <RotateCcw size={12} /> Wyczyść filtry
                </button>
              </div>

              {/* SORTOWANIE + WIDOK — czym układam to, co zostało */}
              <div className="flex items-center gap-2 flex-wrap p-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)] shrink-0">
                  <ArrowUpDown size={12} /> Sortowanie
                </span>
                <select
                  className="input-base text-xs py-1 w-auto"
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as SortField)}
                >
                  {(Object.keys(SORT_LABELS) as SortField[]).map((f) => (
                    <option key={f} value={f}>
                      {SORT_LABELS[f]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() =>
                    setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                  }
                  title={sortDir === "asc" ? "Rosnąco" : "Malejąco"}
                  className="input-base text-xs py-1 px-1.5 inline-flex items-center gap-1"
                >
                  {sortDir === "asc" ? "↑ rosnąco" : "↓ malejąco"}
                </button>
                <div className="ml-auto inline-flex items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                    Widok
                  </span>
                  <div className="inline-flex rounded border border-[var(--color-border-muted)] overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setView("cards")}
                      title="Widok kart"
                      className={`px-2 py-1 text-xs inline-flex items-center gap-1 ${view === "cards" ? "bg-[var(--color-primary)] text-white" : "text-[var(--color-text-muted)]"}`}
                    >
                      <LayoutGrid size={13} /> Karty
                    </button>
                    <button
                      type="button"
                      onClick={() => setView("table")}
                      title="Widok tabeli"
                      className={`px-2 py-1 text-xs inline-flex items-center gap-1 ${view === "table" ? "bg-[var(--color-primary)] text-white" : "text-[var(--color-text-muted)]"}`}
                    >
                      <Table2 size={13} /> Tabela
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {sorted.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)] py-2">
              Brak właścicieli spełniających filtry.
            </p>
          ) : effectiveView === "table" ? (
            <div
              className={`overflow-x-auto border border-[var(--color-border-muted)] rounded ${
                scrollList ? "max-h-[70vh] overflow-y-auto overscroll-contain" : ""
              }`}
            >
              <table className="w-full text-sm">
                <thead
                  className={
                    scrollList
                      ? "sticky top-0 z-10 bg-[var(--color-bg-surface)]"
                      : undefined
                  }
                >
                  <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] border-b border-[var(--color-border-muted)] bg-black/[0.04] dark:bg-white/[0.05]">
                    <th className="px-3 py-2 w-px"></th>
                    <th className="px-3 py-2">Właściciel</th>
                    <th className="px-3 py-2">Podwykonawca</th>
                    <th className="px-3 py-2 w-px whitespace-nowrap">
                      Działka
                    </th>
                    <th className="px-3 py-2 w-px whitespace-nowrap">Status</th>
                    <th className="px-3 py-2 w-px whitespace-nowrap">Pliki</th>
                    <th className="px-3 py-2 w-px whitespace-nowrap">
                      Decyzja GK
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((h) => (
                    <HandoverTableRow key={h.id} h={h} focused={h.id === focusedHandoverId} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className={
                scrollList
                  ? "max-h-[70vh] overflow-y-auto overscroll-contain space-y-2 border border-[var(--color-border-muted)] rounded p-2"
                  : "space-y-2"
              }
            >
              {sorted.map((h) => (
                <HandoverCard key={h.id} h={h} focused={h.id === focusedHandoverId} />
              ))}
            </div>
          )}
        </>
      )}

      {/* project-level rollup — parcels still to be sent into the field for WPW */}
      {connectionId && <WpwNeverSentParcels connectionId={connectionId} />}
    </section>
  );
}
